import { rankChangeDefault, rankChangeWindow, SIGN_OFF_REASONS } from '@felix/domain';
import { Button, Modal, Select, Skeleton, Textarea, TextInput } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import type { TemporaryPasswordDrop } from '../settings/setup-types.js';
import { TemporaryPasswordDroppedNotice } from '../settings/TemporaryPasswordDropped.js';
import classes from './CrewCardDialog.module.css';
import {
  type AmendForm,
  amendFormOf,
  amendRequest,
  emptySignOnForm,
  mlcLimitHint,
  movementErrorKey,
  movementErrorOptions,
  notJoinedYet,
  type SignOffForm,
  type SignOnForm,
  signOffReady,
  signOffRequest,
  signOnReady,
  signOnRequest,
} from './sign-on-off.js';
import { type CrewMovementEditor, errorCode } from './source.js';
import type {
  CrewMovementRow,
  MovementEvent,
  MovementResult,
  MovementTab,
  RankOption,
} from './types.js';

/** Pencerelerdeki seçenek (kişi, gemi). */
export interface Option {
  value: string;
  label: string;
}

/** Pencerelerin üzerinde çalıştığı atama (liste satırı ya da Crew Profile ataması). */
export type MovementTarget = Pick<
  CrewMovementRow,
  | 'assignmentId'
  | 'rowVersion'
  | 'fullName'
  | 'validFrom'
  | 'validTo'
  | 'date'
  | 'port'
  | 'portCode'
  | 'contractNo'
  | 'roleId'
  | 'rank'
>;

/** Sunucu hatasının metni; kararlı başvuru (geçmiş penceresinin etkisi yeniden koşmasın). */
function useMovementError() {
  const { t } = useTranslation();
  return useCallback(
    (error: unknown) =>
      t(movementErrorKey(errorCode(error)), {
        ...movementErrorOptions(error),
        defaultValue: t('signOnOff.errors.GENERIC'),
      }),
    [t],
  );
}

/** Ortak pencere iskeleti: gönderme, hata satırı, Vazgeç / Kaydet. */
function DialogForm({
  title,
  submit,
  ready,
  onClose,
  onSubmit,
  children,
  danger,
}: {
  title: string;
  submit: string;
  ready: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  children: ReactNode;
  danger?: boolean;
}) {
  const { t } = useTranslation();
  const errorText = useMovementError();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={title}
      size="lg"
    >
      <form className={classes.form} onSubmit={send}>
        {children}
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.actions}>
          <Button variant="default" onClick={onClose}>
            {t('signOnOff.dialog.cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={!ready}
            {...(danger ? { color: 'red' } : {})}
          >
            {submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PortFields({
  name,
  code,
  onName,
  onCode,
}: {
  name: string;
  code: string;
  onName: (value: string) => void;
  onCode: (value: string) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string) => t(`signOnOff.dialog.${key}`);
  return (
    <>
      <TextInput
        label={d('port')}
        value={name}
        maxLength={120}
        onChange={(event) => onName(event.currentTarget.value)}
      />
      <TextInput
        label={d('portCode')}
        placeholder={d('portCodePlaceholder')}
        value={code}
        maxLength={5}
        onChange={(event) => onCode(event.currentTarget.value.toUpperCase())}
      />
    </>
  );
}

/** Rütbe listesi (Sign On seçenekleri); `editor` yokken yüklenmez, bir kez yüklenir. */
export function useRanks(editor: CrewMovementEditor | undefined): RankOption[] | null {
  const [ranks, setRanks] = useState<RankOption[] | null>(null);
  useEffect(() => {
    if (!editor || ranks !== null) return;
    let live = true;
    editor
      .ranks()
      .then((list) => live && setRanks(list))
      .catch(() => live && setRanks([]));
    return () => {
      live = false;
    };
  }, [editor, ranks]);
  return ranks;
}

/**
 * Katılışın alanları (kişi hariç): gemi, rütbe, katılış tarihi, kontrat no, liman (ad ve
 * UN/LOCODE), kontrat bitişi; kontrat MLC son gününü aşarsa gerekçe (ADR 0076 §7). Sign On
 * penceresi ve New Crew Card ▸ "Sign on now" (ADR 0131 Ek 4 §2) ortak kullanır; kapsayıcı ızgarayı
 * çağıran verir.
 */
export function SignOnFields({
  form,
  vessels,
  ranks,
  onChange,
}: {
  form: Omit<SignOnForm, 'userId'>;
  vessels: readonly Option[];
  /** `null`: yükleniyor. */
  ranks: readonly RankOption[] | null;
  onChange: (patch: Partial<SignOnForm>) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const hint = mlcLimitHint(form.date, form.contractEnd);
  return (
    <>
      <Select
        label={d('vessel')}
        data={[...vessels]}
        required
        value={form.vesselId || null}
        onChange={(value) => onChange({ vesselId: value ?? '' })}
        comboboxProps={{ withinPortal: true }}
      />
      {ranks === null ? (
        <Skeleton height={60} />
      ) : (
        <Select
          label={d('rank')}
          data={ranks.map((r) => ({ value: r.id, label: r.name }))}
          searchable
          required
          value={form.roleId || null}
          onChange={(value) => onChange({ roleId: value ?? '' })}
          comboboxProps={{ withinPortal: true }}
        />
      )}
      <DayInput
        label={d('joiningDate')}
        required
        value={form.date || null}
        onChange={(value) => onChange({ date: value ?? '' })}
      />
      <TextInput
        label={d('contractNo')}
        value={form.contractNo}
        maxLength={60}
        onChange={(event) => onChange({ contractNo: event.currentTarget.value })}
      />
      <PortFields
        name={form.portName}
        code={form.portCode}
        onName={(portName) => onChange({ portName })}
        onCode={(portCode) => onChange({ portCode })}
      />
      <DayInput
        label={d('contractEnd')}
        clearable
        value={form.contractEnd || null}
        minDate={form.date || undefined}
        onChange={(value) => onChange({ contractEnd: value ?? '' })}
        description={hint ? d('contractReasonHelp', { date: formatDay(hint.limit) }) : undefined}
      />
      {hint?.exceeds ? (
        <Textarea
          className={classes.wide}
          label={d('contractReason')}
          required
          autosize
          minRows={2}
          maxLength={500}
          value={form.contractReason}
          onChange={(event) => onChange({ contractReason: event.currentTarget.value })}
        />
      ) : null}
    </>
  );
}

/** Sign On sonrası belge uyarıları (`PASSPORT_MISSING`, `SEAMAN_BOOK_MISSING`; engel değil). */
export function SignOnWarnings({ warnings }: { warnings: readonly string[] }) {
  const { t } = useTranslation();
  return (
    <>
      <p className={classes.help}>{t('signOnOff.dialog.warnings')}</p>
      <ul className={classes.help}>
        {warnings.map((code) => (
          <li key={code}>
            <IconAlertTriangle size={14} aria-hidden />{' '}
            {t(`signOnOff.warnings.${code}`, { defaultValue: code })}
          </li>
        ))}
      </ul>
    </>
  );
}

/** Kayıttan sonra gösterilecek bilgi: belge uyarıları ve iptal edilen geçici parola. */
export interface MovementSavedNotesData {
  warnings: string[];
  dropped: TemporaryPasswordDrop[];
}

/**
 * Kayıttan sonra bilgi penceresi gerekir mi: Sign On belge uyarılarını (`withWarnings`), Sign On,
 * Rank change, Amend ve Revoke sign-off kişi yönetici hesabına geçince iptal edilen geçici parolayı
 * gösterir (ADR 0150 §6 sınır c). Eski sunucu ve gemi sunucusu alanı göndermez. Gösterilecek bir
 * şey yoksa `null`: pencere hemen kapanır.
 */
export function movementSavedNotes(
  result: Pick<MovementResult, 'warnings' | 'temporaryPasswordDropped'>,
  withWarnings: boolean,
): MovementSavedNotesData | null {
  const warnings = withWarnings ? result.warnings : [];
  const dropped = result.temporaryPasswordDropped ?? [];
  return warnings.length > 0 || dropped.length > 0 ? { warnings, dropped } : null;
}

/** Bilgi penceresinin içeriği. */
export function MovementSavedNotes({ notes }: { notes: MovementSavedNotesData }) {
  return (
    <>
      {notes.warnings.length > 0 ? <SignOnWarnings warnings={notes.warnings} /> : null}
      <TemporaryPasswordDroppedNotice dropped={notes.dropped} />
    </>
  );
}

/** Kayıttan sonraki bilgi penceresi; kapatınca kayıt bitmiş sayılır. */
function MovementSavedDialog({
  title,
  notes,
  onClose,
}: {
  title: string;
  notes: MovementSavedNotesData;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={title}
    >
      <div className={classes.form}>
        <MovementSavedNotes notes={notes} />
        <div className={classes.actions}>
          <Button onClick={onClose}>{t('signOnOff.dialog.close')}</Button>
        </div>
      </div>
    </Modal>
  );
}

/** Kayıttan sonra geçici parola iptali varsa bilgi penceresi (ADR 0150 §6), yoksa hemen `onSaved`. */
function useSavedNotes(onSaved: () => void) {
  const [notes, setNotes] = useState<MovementSavedNotesData | null>(null);
  const finish = (result: MovementResult) => {
    const found = movementSavedNotes(result, false);
    if (found) setNotes(found);
    else onSaved();
  };
  return [notes, finish] as const;
}

/**
 * Sign On ("+ Add Joining", Crew Profile "Sign on", "New Contract"; ADR 0131 §3): kişi (yalnız
 * kartı olanlar), gemi, rütbe, katılış tarihi ve limanı, kontrat bitişi ve numarası; kontrat MLC
 * son gününü aşarsa gerekçe (ADR 0076 §7). Kaydedince belge uyarıları ve iptal edilen geçici
 * parola (ADR 0150 §6) gösterilir.
 */
export function SignOnDialog({
  editor,
  people,
  vessels,
  userId,
  onClose,
  onSaved,
}: {
  editor: CrewMovementEditor;
  people: readonly Option[];
  vessels: readonly Option[];
  /** Crew Profile'dan açılınca kişi sabittir. */
  userId?: string;
  onClose: () => void;
  /** Kayıt yazıldı; `userId` Sign On sonrası hesap önerisi için (ortak 2026-10-30 madde 9). */
  onSaved: (result: MovementResult, userId: string) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const [form, setForm] = useState<SignOnForm>(() => ({
    ...emptySignOnForm(),
    userId: userId ?? '',
    vesselId: vessels.length === 1 ? (vessels[0]?.value ?? '') : '',
  }));
  const ranks = useRanks(editor);
  const [saved, setSaved] = useState<{
    result: MovementResult;
    notes: MovementSavedNotesData;
  } | null>(null);
  const person = people.find((p) => p.value === userId)?.label;

  if (saved) {
    return (
      <MovementSavedDialog
        title={d('signOnTitle')}
        notes={saved.notes}
        onClose={() => onSaved(saved.result, form.userId)}
      />
    );
  }

  return (
    <DialogForm
      title={person ? d('signOnFor', { name: person }) : d('signOnTitle')}
      submit={d('signOn')}
      ready={signOnReady(form)}
      onClose={onClose}
      onSubmit={async () => {
        const result = await editor.signOn(signOnRequest(form));
        const notes = movementSavedNotes(result, true);
        if (notes) setSaved({ result, notes });
        else onSaved(result, form.userId);
      }}
    >
      <div className={classes.grid}>
        {userId ? null : (
          <Select
            className={classes.wide}
            label={d('person')}
            placeholder={d('personPlaceholder')}
            description={d('personHelp')}
            data={[...people]}
            searchable
            required
            value={form.userId || null}
            onChange={(value) => setForm((previous) => ({ ...previous, userId: value ?? '' }))}
            comboboxProps={{ withinPortal: true }}
          />
        )}
        <SignOnFields
          form={form}
          vessels={vessels}
          ranks={ranks}
          onChange={(patch) => setForm((previous) => ({ ...previous, ...patch }))}
        />
      </div>
    </DialogForm>
  );
}

/** Sign Off: tarih (≥ katılış), liman, neden (kapalı liste), not (`other`'da zorunlu). */
export function SignOffDialog({
  editor,
  target,
  onClose,
  onSaved,
}: {
  editor: CrewMovementEditor;
  target: MovementTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const [form, setForm] = useState<SignOffForm>({
    date: '',
    reason: 'end_of_contract',
    remarks: '',
    portName: '',
    portCode: '',
  });
  const set =
    <K extends keyof SignOffForm>(key: K) =>
    (value: SignOffForm[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));
  return (
    <DialogForm
      title={d('signOffTitle', { name: target.fullName })}
      submit={d('signOff')}
      ready={signOffReady(form)}
      onClose={onClose}
      onSubmit={async () => {
        await editor.signOff(target.assignmentId, signOffRequest(form, target.rowVersion));
        onSaved();
      }}
    >
      <div className={classes.grid}>
        <DayInput
          label={d('signOffDate')}
          required
          value={form.date || null}
          minDate={target.validFrom}
          onChange={(value) => set('date')(value ?? '')}
        />
        <Select
          label={d('reason')}
          data={SIGN_OFF_REASONS.map((value) => ({
            value,
            label: t(`signOnOff.reason.${value}`),
          }))}
          required
          allowDeselect={false}
          value={form.reason}
          onChange={(value) => set('reason')(value ?? 'end_of_contract')}
          comboboxProps={{ withinPortal: true }}
        />
        <PortFields
          name={form.portName}
          code={form.portCode}
          onName={set('portName')}
          onCode={set('portCode')}
        />
        <Textarea
          className={classes.wide}
          label={d('remarks')}
          description={form.reason === 'other' ? d('remarksRequired') : undefined}
          required={form.reason === 'other'}
          autosize
          minRows={2}
          maxLength={500}
          value={form.remarks}
          onChange={(event) => set('remarks')(event.currentTarget.value)}
        />
      </div>
    </DialogForm>
  );
}

/**
 * Rank change penceresinin üst satırı: mevcut atamanın katılışı ve rütbesi. Katılmamış kişide Amend
 * yolunu söyler (sahip 2026-10-31: "Tarihler sırasız." ne yapılacağını anlatmıyordu).
 */
export function RankChangeCurrent({
  validFrom,
  rank,
  today,
}: {
  validFrom: string;
  rank: string;
  today: string;
}) {
  const { t } = useTranslation();
  const key = notJoinedYet({ validFrom }, today) ? 'joinsAs' : 'joinedAs';
  return (
    <p className={classes.help}>
      {t(`signOnOff.dialog.${key}`, { date: formatDay(validFrom), rank })}
    </p>
  );
}

/**
 * Rank change: yeni rütbe ve ilk günü; eski kayıt bir gün önce kapanır (ADR 0076 §3). Gün alanı
 * katılış+1 ile (varsa) ayrılış arasındadır; varsayılan bugün, geçersizse katılış+1.
 */
export function RankChangeDialog({
  editor,
  target,
  today,
  onClose,
  onSaved,
}: {
  editor: CrewMovementEditor;
  target: MovementTarget;
  /** Geminin bugünü: varsayılan gün ve katılmamış kişi ipucu. */
  today: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const [ranks, setRanks] = useState<RankOption[] | null>(null);
  const [roleId, setRoleId] = useState('');
  const [date, setDate] = useState(() => rankChangeDefault(target, today));
  const [remarks, setRemarks] = useState('');
  const [notes, finish] = useSavedNotes(onSaved);
  const range = rankChangeWindow(target);
  useEffect(() => {
    let live = true;
    editor
      .ranks()
      .then((list) => live && setRanks(list))
      .catch(() => live && setRanks([]));
    return () => {
      live = false;
    };
  }, [editor]);
  const current = target.rank ?? ranks?.find((r) => r.id === target.roleId)?.name ?? '—';
  if (notes) {
    return (
      <MovementSavedDialog
        title={d('rankChangeTitle', { name: target.fullName })}
        notes={notes}
        onClose={onSaved}
      />
    );
  }
  return (
    <DialogForm
      title={d('rankChangeTitle', { name: target.fullName })}
      submit={d('save')}
      ready={roleId !== '' && date !== ''}
      onClose={onClose}
      onSubmit={async () => {
        finish(
          await editor.rankChange(target.assignmentId, {
            rowVersion: target.rowVersion,
            roleId,
            date,
            ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
          }),
        );
      }}
    >
      <RankChangeCurrent validFrom={target.validFrom} rank={current} today={today} />
      <div className={classes.grid}>
        {ranks === null ? (
          <Skeleton height={60} />
        ) : (
          <Select
            label={d('rank')}
            data={ranks
              .filter((r) => r.id !== target.roleId)
              .map((r) => ({ value: r.id, label: r.name }))}
            searchable
            required
            value={roleId || null}
            onChange={(value) => setRoleId(value ?? '')}
            comboboxProps={{ withinPortal: true }}
          />
        )}
        <DayInput
          label={d('newRankFrom')}
          description={d('newRankHelp')}
          required
          minDate={range.first}
          maxDate={range.last ?? undefined}
          value={date || null}
          onChange={(value) => setDate(value ?? '')}
        />
        <Textarea
          className={classes.wide}
          label={d('remarks')}
          autosize
          minRows={2}
          maxLength={500}
          value={remarks}
          onChange={(event) => setRemarks(event.currentTarget.value)}
        />
      </div>
    </DialogForm>
  );
}

/** Amend: sekmenin tarihi ve limanı, kontrat numarası; gerçekleşmiş harekette gerekçe zorunlu. */
export function AmendDialog({
  editor,
  target,
  tab,
  today,
  onClose,
  onSaved,
}: {
  editor: CrewMovementEditor;
  target: MovementTarget;
  tab: MovementTab;
  /** Geminin bugünü: gerekçe zorunluluğunun ipucu (kararı sunucu verir). */
  today: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const row = { ...target } as CrewMovementRow;
  const [form, setForm] = useState<AmendForm>(() => amendFormOf(row));
  const set =
    <K extends keyof AmendForm>(key: K) =>
    (value: AmendForm[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));
  const happened =
    target.validFrom <= today || (target.validTo !== null && target.validTo <= today);
  // Rütbe Amend ile düzeltilir; katılmamış kişide terfi değil düzeltmedir (sahip 2026-10-31)
  const [ranks, setRanks] = useState<RankOption[] | null>(null);
  const [notes, finish] = useSavedNotes(onSaved);
  useEffect(() => {
    let live = true;
    editor
      .ranks()
      .then((list) => live && setRanks(list))
      .catch(() => live && setRanks([]));
    return () => {
      live = false;
    };
  }, [editor]);
  if (notes) {
    return (
      <MovementSavedDialog
        title={d('amendTitle', { name: target.fullName })}
        notes={notes}
        onClose={onSaved}
      />
    );
  }
  return (
    <DialogForm
      title={d('amendTitle', { name: target.fullName })}
      submit={d('save')}
      ready={form.date !== '' && (!happened || form.reason.trim() !== '')}
      onClose={onClose}
      onSubmit={async () => {
        finish(await editor.amend(target.assignmentId, amendRequest(row, tab, form)));
      }}
    >
      <div className={classes.grid}>
        <DayInput
          label={d(tab === 'joining' ? 'joiningDate' : 'signOffDate')}
          required
          value={form.date || null}
          onChange={(value) => set('date')(value ?? '')}
        />
        {ranks === null ? (
          <Skeleton height={60} />
        ) : (
          <Select
            label={d('rank')}
            data={ranks.map((r) => ({ value: r.id, label: r.name }))}
            searchable
            required
            value={form.roleId || null}
            onChange={(value) => set('roleId')(value ?? target.roleId)}
            comboboxProps={{ withinPortal: true }}
          />
        )}
        <TextInput
          label={d('contractNo')}
          value={form.contractNo}
          maxLength={60}
          onChange={(event) => set('contractNo')(event.currentTarget.value)}
        />
        <PortFields
          name={form.portName}
          code={form.portCode}
          onName={set('portName')}
          onCode={set('portCode')}
        />
        <Textarea
          className={classes.wide}
          label={d('correctionReason')}
          description={d('correctionHelp')}
          required={happened}
          autosize
          minRows={2}
          maxLength={500}
          value={form.reason}
          onChange={(event) => set('reason')(event.currentTarget.value)}
        />
      </div>
    </DialogForm>
  );
}

/** Cancel joining ve Revoke sign-off: gerekçe (iptalde her zaman, geri almada gerçekleşmişse). */
export function ReasonDialog({
  editor,
  target,
  mode,
  today,
  onClose,
  onSaved,
}: {
  editor: CrewMovementEditor;
  target: MovementTarget;
  mode: 'cancel' | 'revoke';
  today: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const [reason, setReason] = useState('');
  const [notes, finish] = useSavedNotes(onSaved);
  const required = mode === 'cancel' || (target.validTo !== null && target.validTo <= today);
  const title = d(mode === 'cancel' ? 'cancelTitle' : 'revokeTitle', { name: target.fullName });
  if (notes) return <MovementSavedDialog title={title} notes={notes} onClose={onSaved} />;
  return (
    <DialogForm
      title={title}
      submit={d(mode === 'cancel' ? 'confirmCancel' : 'confirmRevoke')}
      ready={!required || reason.trim() !== ''}
      danger
      onClose={onClose}
      onSubmit={async () => {
        finish(
          mode === 'cancel'
            ? await editor.cancel(target.assignmentId, {
                rowVersion: target.rowVersion,
                reason: reason.trim(),
              })
            : await editor.revoke(target.assignmentId, {
                rowVersion: target.rowVersion,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              }),
        );
      }}
    >
      <p className={classes.help}>{d(mode === 'cancel' ? 'cancelHelp' : 'revokeHelp')}</p>
      <Textarea
        label={d('reason')}
        required={required}
        autosize
        minRows={2}
        maxLength={500}
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
      />
    </DialogForm>
  );
}

/** Atamanın olay geçmişi (değiştirilemez `crew_movement_events`; yalnız ofiste). */
export function HistoryDialog({
  editor,
  target,
  onClose,
}: {
  editor: CrewMovementEditor;
  target: Pick<MovementTarget, 'assignmentId' | 'fullName'>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`signOnOff.dialog.${key}`, options);
  const errorText = useMovementError();
  const [events, setEvents] = useState<MovementEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    editor
      .events(target.assignmentId)
      .then((list) => live && setEvents(list))
      .catch((caught: unknown) => live && setError(errorText(caught)));
    return () => {
      live = false;
    };
  }, [editor, target.assignmentId, errorText]);
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={d('historyTitle', { name: target.fullName })}
      size="lg"
    >
      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : events === null ? (
        <Skeleton height={120} />
      ) : events.length === 0 ? (
        <p className={classes.help}>{d('historyEmpty')}</p>
      ) : (
        <ol className={classes.form} aria-label={d('historyTitle', { name: target.fullName })}>
          {events.map((event) => (
            <li key={event.id}>
              <strong>{t(`signOnOff.kind.${event.kind}`)}</strong> · {formatDay(event.eventDate)}
              {event.port ? ` · ${event.port}${event.portCode ? ` (${event.portCode})` : ''}` : ''}
              {event.reason
                ? ` · ${t(`signOnOff.reason.${event.reason}`, { defaultValue: event.reason })}`
                : ''}
              {event.remarks ? <p className={classes.help}>{event.remarks}</p> : null}
              <p className={classes.help}>
                {formatDay(event.createdAt.slice(0, 10))}
                {event.createdByName ? ` · ${d('by', { name: event.createdByName })}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
